import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { Cancel, Done, SpeakerGroup } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  Grid,
  FormControlLabel,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material'
import { CommonContent } from '../components/CommonContent'
import CheckboxSelector from '../components/CheckboxSelector'
import { devicesState } from '../common/store/selectors/devices'
import { ipdevicesRequest } from '../common/store/actions/devices/actions'
import { groupsStates } from '../common/store/selectors/groups'
import { NewZone } from '../common/api/types'
import {
  groupCreateRequest,
  groupRequest,
  groupUpdateRequest,
} from '../common/store/actions/groups/actions'

const isMulticast = process.env?.REACT_APP_PAGING_MODE == 'MULTICAST'

export type ZoneFormValues = {
  name: string
  description: string
  ipMulticast?: string
  portMulticast?: number
}

export const ZoneForm = () => {
  const { id } = useParams()
  const isEdit = id !== 'add'

  useEffect(() => {
    if (!ipDevices.items.length) {
      dispatch(ipdevicesRequest({ limit: '200', page: '1' }))
    }
    if (isEdit) {
      dispatch(groupRequest(Number(id)))
    }
  }, [id])

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { ipDevices } = useSelector(devicesState)
  const { group, isLoading } = useSelector(groupsStates)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [isMusic, setIsMusic] = useState<boolean>(false)
  const { control, handleSubmit, reset, getValues } = useForm<ZoneFormValues>()

  useEffect(() => {
    if (isEdit && group) {
      reset({
        name: group.name,
        description: group.description,
        ipMulticast: group.ipMulticast || '',
        portMulticast: group.portMulticast || undefined,
      })
      const devices = group.devices.map((device) => device.id)
      setSelectedItems(devices)
      if (group.isMusic && isMulticast) setIsMusic(true)
    }
  }, [group, isEdit, reset])

  const handleCheckboxChange = (e: any) => {
    const { checked } = e.target
    setIsMusic(checked)
  }

  const onSubmit = handleSubmit((data: ZoneFormValues) => {
    if (data) {
      const newZone: NewZone = {
        name: data.name,
        description: data.description,
        devices: selectedItems,
        isMusic: isMusic,
      }

      if (isMusic && isMulticast) {
        newZone.ipMulticast = data.ipMulticast
        newZone.portMulticast = data.portMulticast
      }

      if (isEdit) {
        const newData = {
          body: newZone,
          id: group.id,
        }
        console.log(newData)
        dispatch(groupUpdateRequest(newData))
      } else {
        dispatch(groupCreateRequest(newZone))
      }
      navigate('/groups')
      reset({
        name: '',
        description: '',
        ipMulticast: '',
        portMulticast: null,
      })
      setSelectedItems([])
      setIsMusic(false)
    }
  })

  return (
    <CommonContent
      title={isEdit ? t('edit_zone') : t('zones')}
      titleIcon={<SpeakerGroup style={{ color: 'white' }} />}
    >
      {isLoading ? (
        <CircularProgress style={{ margin: 'auto' }} />
      ) : (
        <form>
          <Controller
            name="name"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('required'),
              },
              validate: () => {
                const name = getValues('name')
                if (name.trim() !== name) return t('check_empty')
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                data-e2e="zones-fld-name"
                label={isEdit ? t('update_name') : t('name')}
                value={value}
                defaultValue={isEdit ? group?.name : ''}
                onChange={onChange}
                helperText={error ? error.message : null}
                error={!!error}
                style={{ paddingBottom: 20 }}
                size="medium"
                fullWidth
                variant="outlined"
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('required'),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                data-e2e="zones-fld-description"
                label={isEdit ? t('update_desc') : t('desc')}
                value={value}
                defaultValue={isEdit ? group?.description : ''}
                onChange={onChange}
                helperText={error ? error.message : null}
                error={!!error}
                style={{ paddingBottom: 20 }}
                fullWidth
                size="small"
                variant="outlined"
              />
            )}
          />
          {isMulticast && (
            <FormControlLabel
              style={{ marginLeft: 20 }}
              control={<Checkbox checked={isMusic} onChange={handleCheckboxChange} />}
              label={t('is_music')}
              data-e2e="checkbox-isMusic"
            />
          )}
          {isMusic && (
            <>
              <Controller
                name="ipMulticast"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    data-e2e="zones-fld-ipMulticast"
                    label={isEdit ? t('update_ipMulticast') : t('ipMulticast')}
                    value={value}
                    defaultValue={isEdit ? group?.ipMulticast : ''}
                    onChange={onChange}
                    style={{ paddingBottom: 20 }}
                    fullWidth
                    placeholder="224.0.0.10"
                    helperText={error ? error.message : null}
                    error={!!error}
                    size="small"
                    variant="outlined"
                  />
                )}
              />
              <Controller
                name="portMulticast"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    data-e2e="zones-fld-portMulticast"
                    label={isEdit ? t('update_portMulticast') : t('portMulticast')}
                    value={value}
                    defaultValue={isEdit ? group?.portMulticast : ''}
                    onChange={onChange}
                    style={{ paddingBottom: 20 }}
                    fullWidth
                    placeholder="52100"
                    helperText={error ? error.message : null}
                    error={!!error}
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            </>
          )}

          <Box mt={4} border={'1px solid lightgrey'} borderRadius="5px" m={4} p={5}>
            <CheckboxSelector
              items={ipDevices.items}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          </Box>

          <Grid container direction="row" justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                onClick={() => {
                  navigate('/groups')
                }}
                variant="outlined"
                color="secondary"
                startIcon={<Cancel />}
              >
                {t('cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedItems.length}
                onClick={onSubmit}
                startIcon={<Done />}
                data-e2e="zones-btn-add"
              >
                {isEdit ? t('save') : t('add')}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </CommonContent>
  )
}
